import React, { useState } from 'react';
import { DataTable } from '../components/DataTable';
import { WizardForm } from '../components/WizardForm';
import { StatusBadge } from '../components/StatusBadge';
import { UserIcon, PhoneIcon, MapPinIcon, BriefcaseIcon, XIcon } from 'lucide-react';
import { beneficiaries } from '../data/beneficiaries';
export function ConservationManagement() {
  const [activeTab, setActiveTab] = useState<'list' | 'add' | 'approval'>('list');
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedBeneficiaries, setSelectedBeneficiaries] = useState<string[]>([]);
  const [beneficiarySearch, setBeneficiarySearch] = useState('');
  const [conservationsList, setConservationsList] = useState([{
    groupType: 'CPG',
    groupName: 'Tiger Monitoring Zone A',
    division: 'Khulna',
    range: 'Satkhira Range',
    station: 'Burigoalini Station',
    formationDate: '2024-01-10',
    status: 'active' as const
  }, {
    groupType: 'DCT',
    groupName: 'Dolphin Protection Drive',
    division: 'Khulna',
    range: 'Khulna Range',
    station: 'Karamjal Station',
    formationDate: '2024-02-15',
    status: 'active' as const
  }, {
    groupType: 'CPG',
    groupName: 'Sundarban Patrol Route B',
    division: 'Bagerhat',
    range: 'Chandpai Range',
    station: 'Harbaria Station',
    formationDate: '2024-01-01',
    status: 'completed' as const
  }, {
    groupType: 'VTRT',
    groupName: 'Eco Tour - Mangrove Trail',
    division: 'Satkhira',
    range: 'Sarankhola Range',
    station: 'Kotka Station',
    formationDate: '2024-03-05',
    status: 'active' as const
  }, {
    groupType: 'VTRT',
    groupName: 'Biodiversity Research 2024',
    division: 'Khulna',
    range: 'Chandpai Range',
    station: 'Joymoni Station',
    formationDate: '2024-04-01',
    status: 'pending' as const
  }, {
    groupType: 'CPG',
    groupName: 'Anti-Poaching Patrol C',
    division: 'Satkhira',
    range: 'Satkhira Range',
    station: 'Kaikhali Station',
    formationDate: '2024-02-20',
    status: 'cancelled' as const
  }]);
  const [formData, setFormData] = useState({
    conservationGroupType: '',
    groupName: '',
    division: '',
    range: '',
    station: '',
    formationDate: '',
    status: 'Active'
  });
  const stationsByRange: Record<string, string[]> = {
    'Satkhira Range': ['Burigoalini Station', 'Kaikhali Station', 'Dobeki Station'],
    'Khulna Range': ['Nalian Station', 'Patkosta Station', 'Karamjal Station'],
    'Chandpai Range': ['Harbaria Station', 'Joymoni Station', 'Tamulbunia Station'],
    'Sarankhola Range': ['Supati Station', 'Sharankhola Station', 'Kotka Station']
  };
  // Filter approved beneficiaries for association
  const approvedBeneficiaries = beneficiaries.filter(b => b.status === 'approved');
  const filteredBeneficiaries = approvedBeneficiaries.filter(b => b.name.toLowerCase().includes(beneficiarySearch.toLowerCase()) || b.nid.includes(beneficiarySearch) || b.profession.toLowerCase().includes(beneficiarySearch.toLowerCase()));
  const pendingApprovals = conservationsList.filter(c => c.status === 'pending');
  const columns = [{
    key: 'groupType',
    label: 'Group Type'
  }, {
    key: 'groupName',
    label: 'Group Name'
  }, {
    key: 'division',
    label: 'Division'
  }, {
    key: 'range',
    label: 'Range'
  }, {
    key: 'station',
    label: 'Station'
  }, {
    key: 'formationDate',
    label: 'Formation Date'
  }, {
    key: 'status',
    label: 'Status',
    render: (value: string) => <StatusBadge status={value as any} data-id="element-697" data-mp="9zca6alxggav" />
  }];
  const filters = [{
    key: 'groupType',
    label: 'Group Type',
    options: ['CPG', 'VTRT', 'DCT']
  }, {
    key: 'status',
    label: 'Status',
    options: ['Active', 'Completed', 'Cancelled', 'Pending']
  }];
  const wizardSteps = [{
    title: 'Conservation Info',
    description: 'Enter basic conservation details'
  }, {
    title: 'Resource Mapping',
    description: 'Add beneficiary associates to the group'
  }, {
    title: 'Review & Submit',
    description: 'Review all information before submitting'
  }];
  return <div className="space-y-6" data-id="element-698" data-mp="qkl0yx9d1l3e">
      <div data-id="element-699" data-mp="qj9v8rwcu39n">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-700" data-mp="qhqgj5qck09z">
          Conservation Management
        </h1>
        <p className="text-sm text-gray-600 mt-1" data-id="element-701" data-mp="15as6kce9r9mo">
          Manage conservation projects, patrols, and eco-tours
        </p>
      </div>

      <div className="border-b border-gray-200" data-id="element-702" data-mp="1e8vvzjsdkqjr">
        <div className="flex gap-4" data-id="element-703" data-mp="1e9nf3v4scvt5">
          <button onClick={() => setActiveTab('list')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'list' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-704" data-mp="1a8282qexnbqv">
            Conservation List
          </button>
          <button onClick={() => setActiveTab('add')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'add' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-705" data-mp="1jucxl4o0w4wl">
            Add Conservation
          </button>
          <button onClick={() => setActiveTab('approval')} className={`px-4 py-2 border-b-2 font-medium text-sm transition-colors ${activeTab === 'approval' ? 'border-[#1B5E20] text-[#1B5E20]' : 'border-transparent text-gray-600 hover:text-gray-900'}`} data-id="element-706" data-mp="1exmhwig4rbbp">
            Approval Queue ({pendingApprovals.length})
          </button>
        </div>
      </div>

      {activeTab === 'list' && <DataTable columns={columns} data={conservationsList} filters={filters} onDataChange={setConservationsList} searchPlaceholder="Search conservations..." data-id="element-707" data-mp="w7ocka3pvwbr" />}

      {activeTab === 'approval' && <DataTable columns={columns} data={pendingApprovals} onView={row => console.log('View', row)} onApprove={row => {
      setConservationsList(conservationsList.map(c => c.groupName === row.groupName ? {
        ...c,
        status: 'active' as const
      } : c));
      alert(`Approved: ${row.groupName}`);
    }} searchPlaceholder="Search pending approvals..." data-id="element-708" data-mp="2p37992qjxgz4" />}

      {activeTab === 'add' && <WizardForm steps={wizardSteps} currentStep={currentStep} onStepChange={setCurrentStep} onSubmit={() => {
      if (!formData.conservationGroupType || !formData.groupName || !formData.division) {
        alert('Please fill in all required fields.');
        return;
      }
      // Add new conservation to the list
      setConservationsList([...conservationsList, {
        groupType: formData.conservationGroupType,
        groupName: formData.groupName,
        division: formData.division,
        range: formData.range,
        station: formData.station,
        formationDate: formData.formationDate,
        status: 'pending' as const
      }]);
      alert(`Conservation Group "${formData.groupName}" submitted for approval!`);
      setCurrentStep(0);
      setActiveTab('approval');
      setSelectedBeneficiaries([]);
      setBeneficiarySearch('');
      setFormData({
        conservationGroupType: '',
        groupName: '',
        division: '',
        range: '',
        station: '',
        formationDate: '',
        status: 'Active'
      });
    }} data-id="element-709" data-mp="1rex7mouogv8j">
          {currentStep === 0 && <div className="space-y-4" data-id="element-710" data-mp="2rb33qvsoqaal">
              <div className="grid grid-cols-2 gap-4" data-id="element-711" data-mp="23ivyt88w8uzk">
                <div data-id="element-712" data-mp="23li6r8zp5cib">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-713" data-mp="1ykb05d9aijd8">
                    Conservation Group Type *
                  </label>
                  <select required value={formData.conservationGroupType} onChange={e => setFormData({
              ...formData,
              conservationGroupType: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-714" data-mp="2clnexlcpsexj">
                    <option value="" data-id="element-715" data-mp="bn9fjetfgmq">Select Group Type</option>
                    <option value="CPG" data-id="element-716" data-mp="3hj8whxv1vh33">CPG (Community Patrol Group)</option>
                    <option value="VTRT" data-id="element-717" data-mp="3hldbbauhm0i5">
                      VTRT (Village Tiger Response Team)
                    </option>
                    <option value="DCT" data-id="element-718" data-mp="e1qpttr0kg49">DCT (Dolphin Conservation Team)</option>
                  </select>
                </div>
                <div data-id="element-719" data-mp="j01yw29himlf">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-720" data-mp="j0lgfhv838yx">
                    Group Name *
                  </label>
                  <input type="text" required maxLength={150} value={formData.groupName} onChange={e => setFormData({
              ...formData,
              groupName: e.target.value
            })} placeholder="Max 150 characters" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-721" data-mp="9ba8hyk2oacm" />
                  <p className="text-xs text-gray-400 mt-1" data-id="element-722" data-mp="tjrcg0iwslhs">
                    {formData.groupName.length}/150
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4" data-id="element-723" data-mp="esqvf743jja1">
                <div data-id="element-724" data-mp="jltufurn7kxm">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-725" data-mp="jn4vhmtpeel1">
                    Division *
                  </label>
                  <select required value={formData.division} onChange={e => setFormData({
              ...formData,
              division: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-726" data-mp="at9osacxog3a">
                    <option value="" data-id="element-727" data-mp="39enww6pv6kht">Select Division</option>
                    <option value="Khulna" data-id="element-728" data-mp="3nd4bskxvrhb1">Khulna</option>
                    <option value="Satkhira" data-id="element-729" data-mp="3nahs5mxptbcg">Satkhira</option>
                    <option value="Bagerhat" data-id="element-730" data-mp="3icztcpdewci6">Bagerhat</option>
                  </select>
                </div>
                <div data-id="element-731" data-mp="6lfeqf582js">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-732" data-mp="61wb6drb846">
                    Range
                  </label>
                  <select value={formData.range} onChange={e => setFormData({
              ...formData,
              range: e.target.value,
              station: ''
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-733" data-mp="9yhfu84pc4r6">
                    <option value="" data-id="element-734" data-mp="lyecrfs0p8iu">Select Range</option>
                    <option value="Satkhira Range" data-id="element-735" data-mp="r03qj36mk0am">Satkhira Range</option>
                    <option value="Khulna Range" data-id="element-736" data-mp="vxwtzogjmg77">Khulna Range</option>
                    <option value="Chandpai Range" data-id="element-737" data-mp="vwlqzqux9ch5">Chandpai Range</option>
                    <option value="Sarankhola Range" data-id="element-738" data-mp="10qg8qoqyvhdl">Sarankhola Range</option>
                  </select>
                </div>
                <div data-id="element-739" data-mp="15qko2bg52628">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-740" data-mp="19pacr06mna16">
                    Station
                  </label>
                  <select value={formData.station} onChange={e => setFormData({
              ...formData,
              station: e.target.value
            })} disabled={!formData.range} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20] disabled:bg-gray-100 disabled:text-gray-400" data-id="element-741" data-mp="1eq7q8zgozfwc">
                    <option value="" data-id="element-742" data-mp="3931xmdyuv5md">
                      {formData.range ? 'Select Station' : 'Select Range first'}
                    </option>
                    {formData.range && stationsByRange[formData.range]?.map(station => <option key={station} value={station} data-id="element-743" data-mp="o0bx7qarnuj">
                          {station}
                        </option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4" data-id="element-744" data-mp="3muoticovyc4m">
                <div data-id="element-745" data-mp="3hxyhc0g7c0oh">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-746" data-mp="3hwn99ofq46rk">
                    Formation Date
                  </label>
                  <input type="date" value={formData.formationDate} onChange={e => setFormData({
              ...formData,
              formationDate: e.target.value
            })} className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20]" data-id="element-747" data-mp="2bf08hqv6zn6g" />
                </div>
                <div data-id="element-748" data-mp="3qb1ewrsdc6c0">
                  <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-749" data-mp="hx18y3brtz88">
                    Status
                  </label>
                  <div className="flex items-center gap-4 mt-2" data-id="element-750" data-mp="mvcliltganu5">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-751" data-mp="7zwlm066vxpu">
                      <input type="radio" name="conservationStatus" value="Active" checked={formData.status === 'Active'} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} className="w-4 h-4 text-[#1B5E20] focus:ring-[#1B5E20]" data-id="element-752" data-mp="cyhjhsxyy5b4" />
                      <span className="text-sm text-gray-700" data-id="element-753" data-mp="1oqgzsjh7yw7x">Active</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-754" data-mp="1kqokzp18ftu4">
                      <input type="radio" name="conservationStatus" value="Inactive" checked={formData.status === 'Inactive'} onChange={e => setFormData({
                  ...formData,
                  status: e.target.value
                })} className="w-4 h-4 text-[#1B5E20] focus:ring-[#1B5E20]" data-id="element-755" data-mp="x1njklqthiyc" />
                      <span className="text-sm text-gray-700" data-id="element-756" data-mp="996ys3fp3cvc">Inactive</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>}

          {currentStep === 1 && <div className="space-y-4" data-id="element-757" data-mp="2i5b64cxig1jd">
              <div data-id="element-758" data-mp="2i6ctngvpqghz">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-759" data-mp="2i7xeges86l2n">
                  Add Beneficiary Associates
                </label>
                <input type="text" value={beneficiarySearch} onChange={e => setBeneficiarySearch(e.target.value)} placeholder="Search by name, NID, or profession..." className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5E20] mb-2" data-id="element-760" data-mp="1phyu2woz7c4b" />
                <div className="border border-gray-300 rounded-lg p-3 space-y-2 max-h-48 overflow-y-auto" data-id="element-761" data-mp="3at719d7flle1">
                  {filteredBeneficiaries.map(beneficiary => <label key={beneficiary.nid} className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer transition-colors ${selectedBeneficiaries.includes(beneficiary.nid) ? 'bg-green-50 border border-green-200' : 'hover:bg-gray-50 border border-transparent'}`} data-id="element-762" data-mp="145q40y4ybd64">
                      <input type="checkbox" checked={selectedBeneficiaries.includes(beneficiary.nid)} onChange={e => {
                if (e.target.checked) {
                  setSelectedBeneficiaries([...selectedBeneficiaries, beneficiary.nid]);
                } else {
                  setSelectedBeneficiaries(selectedBeneficiaries.filter(id => id !== beneficiary.nid));
                }
              }} className="w-4 h-4 text-[#1B5E20] border-gray-300 rounded focus:ring-[#1B5E20]" data-id="element-763" data-mp="pgaf7nozdht2" />
                      <div className="w-8 h-8 bg-[#1B5E20]/10 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-764" data-mp="1506cm9ioiw20">
                        <UserIcon className="w-4 h-4 text-[#1B5E20]" data-id="element-765" data-mp="r05dozlz5yp6" />
                      </div>
                      <div className="flex-1" data-id="element-766" data-mp="vzrrba5ptgyi">
                        <span className="text-sm font-medium text-gray-900" data-id="element-767" data-mp="w0t0f9367tdl">
                          {beneficiary.name}
                        </span>
                        <span className="text-xs text-gray-500 ml-2" data-id="element-768" data-mp="m0ipjv4lyy1l">
                          {beneficiary.profession} · {beneficiary.village}
                        </span>
                      </div>
                    </label>)}
                  {filteredBeneficiaries.length === 0 && <p className="text-sm text-gray-500 text-center py-2" data-id="element-769" data-mp="4t18vi82thjc">
                      No beneficiaries found
                    </p>}
                </div>
                {selectedBeneficiaries.length > 0 && <p className="text-xs text-[#1B5E20] mt-1 font-medium" data-id="element-770" data-mp="ejvy8vgmcx90">
                    {selectedBeneficiaries.length} beneficiar
                    {selectedBeneficiaries.length > 1 ? 'ies' : 'y'} selected
                  </p>}
              </div>

              {/* Selected Beneficiaries Information */}
              {selectedBeneficiaries.length > 0 && <div className="space-y-3" data-id="element-771" data-mp="xfvskcx2kcvr">
                  <h4 className="text-sm font-semibold text-gray-900 flex items-center gap-2" data-id="element-772" data-mp="shu0x3lkv58o">
                    <UserIcon className="w-4 h-4 text-[#1B5E20]" data-id="element-773" data-mp="2z7s0lck134ls" />
                    Selected Beneficiary Associates
                  </h4>
                  {selectedBeneficiaries.map(nid => {
            const beneficiary = approvedBeneficiaries.find(b => b.nid === nid);
            if (!beneficiary) return null;
            return <div key={nid} className="bg-green-50 border border-green-200 rounded-lg p-4" data-id="element-774" data-mp="zifolj28dk5l">
                        <div className="flex items-start gap-4" data-id="element-775" data-mp="6tijkhn15jus">
                          <div className="w-12 h-12 bg-[#1B5E20]/10 rounded-full flex items-center justify-center flex-shrink-0" data-id="element-776" data-mp="6tsaq8jt9x22">
                            <UserIcon className="w-6 h-6 text-[#1B5E20]" data-id="element-777" data-mp="grokox10ilyl" />
                          </div>
                          <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm flex-1" data-id="element-778" data-mp="bsv3oumiocto">
                            <div data-id="element-779" data-mp="1s6uzbtebxgb4">
                              <span className="text-gray-500" data-id="element-780" data-mp="1s5sb5t8e5ryw">Name:</span>{' '}
                              <span className="font-medium text-gray-900" data-id="element-781" data-mp="1n8a09xatd2o9">
                                {beneficiary.name}
                              </span>
                            </div>
                            <div data-id="element-782" data-mp="3a4836crmu15c">
                              <span className="text-gray-500" data-id="element-783" data-mp="3a59gknw4ezq8">NID:</span>{' '}
                              <span className="font-medium text-gray-900" data-id="element-784" data-mp="2w5gvp979zdq6">
                                {beneficiary.nid}
                              </span>
                            </div>
                            <div data-id="element-785" data-mp="3osunksw49j3f">
                              <span className="text-gray-500" data-id="element-786" data-mp="3otw77lr77dwr">Phone:</span>{' '}
                              <span className="font-medium text-gray-900" data-id="element-787" data-mp="3stwmf8m4ft4v">
                                {beneficiary.phone}
                              </span>
                            </div>
                            <div data-id="element-788" data-mp="3k1mbtq7bdln3">
                              <span className="text-gray-500" data-id="element-789" data-mp="3k0jkjh62xh6n">Village:</span>{' '}
                              <span className="font-medium text-gray-900" data-id="element-790" data-mp="bl9nwokyl469">
                                {beneficiary.village}
                              </span>
                            </div>
                            <div data-id="element-791" data-mp="q91aen9ewzna">
                              <span className="text-gray-500" data-id="element-792" data-mp="q76x9v4dubtr">Profession:</span>{' '}
                              <span className="font-medium text-gray-900" data-id="element-793" data-mp="l8djs2p3iktl">
                                {beneficiary.profession}
                              </span>
                            </div>
                            <div data-id="element-794" data-mp="bedb5quluxg4">
                              <span className="text-gray-500" data-id="element-795" data-mp="7b76gnn55ebr">Email:</span>{' '}
                              <span className="font-medium text-gray-900" data-id="element-796" data-mp="2jyx1rlt9syl">
                                {beneficiary.email}
                              </span>
                            </div>
                          </div>
                          <button type="button" onClick={() => setSelectedBeneficiaries(selectedBeneficiaries.filter(id => id !== nid))} className="text-gray-400 hover:text-red-500 transition-colors flex-shrink-0" title="Remove beneficiary" data-id="element-797" data-mp="3oryh8t23ziy7">
                            <XIcon className="w-4 h-4" data-id="element-798" data-mp="297z7xb6ws3eq" />
                          </button>
                        </div>
                      </div>;
          })}
                </div>}
            </div>}

          {currentStep === 2 && <div className="space-y-6" data-id="element-799" data-mp="1lin12hz207fl">
              <div className="bg-gray-50 rounded-lg p-4" data-id="element-800" data-mp="1lhlog92e3cvr">
                <div className="flex items-center justify-between mb-3" data-id="element-801" data-mp="1q9d43ok0by74">
                  <h4 className="font-semibold text-gray-900" data-id="element-802" data-mp="1qaobrzgkpq3i">
                    Conservation Info
                  </h4>
                  <button onClick={() => setCurrentStep(0)} className="text-sm text-[#1B5E20] hover:underline" data-id="element-803" data-mp="xt2gxvr5z44q">
                    Edit
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm" data-id="element-804" data-mp="2vho6euaybu78">
                  <div data-id="element-805" data-mp="397rl907i4lmn">
                    <span className="text-gray-600" data-id="element-806" data-mp="3992yrrqr17qy">Group Type:</span>{' '}
                    <span className="font-medium" data-id="element-807" data-mp="34hkwn0czp14e">
                      {formData.conservationGroupType || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-808" data-mp="3j81xahhsvn08">
                    <span className="text-gray-600" data-id="element-809" data-mp="3e7e5vupnt45h">Group Name:</span>{' '}
                    <span className="font-medium" data-id="element-810" data-mp="3e9gv4cizmt95">
                      {formData.groupName || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-811" data-mp="3n93yh3va294u">
                    <span className="text-gray-600" data-id="element-812" data-mp="3n5pvpuipbxwv">Division:</span>{' '}
                    <span className="font-medium" data-id="element-813" data-mp="1xtns1w8qn1in">
                      {formData.division || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-814" data-mp="8c0jbb4zlqao">
                    <span className="text-gray-600" data-id="element-815" data-mp="8cs925bwktt1">Range:</span>{' '}
                    <span className="font-medium" data-id="element-816" data-mp="dak9jnqm5o8u">
                      {formData.range || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-817" data-mp="3vokujly1rpb0">
                    <span className="text-gray-600" data-id="element-818" data-mp="3l0qagyznsdg">Station:</span>{' '}
                    <span className="font-medium" data-id="element-819" data-mp="3j7jn1odl85g">
                      {formData.station || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-820" data-mp="3qq03ac0rwfyp">
                    <span className="text-gray-600" data-id="element-821" data-mp="3qrb7gcy4rpqc">Formation Date:</span>{' '}
                    <span className="font-medium" data-id="element-822" data-mp="3cr0g78sws94n">
                      {formData.formationDate || 'Not provided'}
                    </span>
                  </div>
                  <div data-id="element-823" data-mp="1aylxi7ijj0n6">
                    <span className="text-gray-600" data-id="element-824" data-mp="1aycamsssl9lp">Status:</span>{' '}
                    <span className={`font-medium ${formData.status === 'Active' ? 'text-green-700' : 'text-gray-500'}`} data-id="element-825" data-mp="1f076hhryeus3">
                      {formData.status}
                    </span>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4" data-id="element-826" data-mp="2ewfyachw30by">
                <div className="flex items-center justify-between mb-3" data-id="element-827" data-mp="2ewzfts3mnmpg">
                  <h4 className="font-semibold text-gray-900" data-id="element-828" data-mp="29y4kxrsdy7oz">
                    Beneficiary Associates
                  </h4>
                  <button onClick={() => setCurrentStep(1)} className="text-sm text-[#1B5E20] hover:underline" data-id="element-829" data-mp="36n3uda4phfir">
                    Edit
                  </button>
                </div>
                <div className="text-sm" data-id="element-830" data-mp="2swgx7melfxos">
                  <span className="text-gray-600" data-id="element-831" data-mp="2svfmxk5ejf84">Associates:</span>{' '}
                  <span className="font-medium" data-id="element-832" data-mp="3qbuu3cyd3m5h">
                    {selectedBeneficiaries.length > 0 ? selectedBeneficiaries.map(nid => {
                const b = approvedBeneficiaries.find(ben => ben.nid === nid);
                return b ? b.name : nid;
              }).join(', ') : 'Not provided'}
                  </span>
                </div>
              </div>
            </div>}
        </WizardForm>}
    </div>;
}